'use client';

import { Dialog, DialogPanel } from '@headlessui/react';
import { BriefcaseIcon, MapPinIcon, WifiIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import Button from '@/components/ui/Button';
import Container from '@/components/ui/Container';
import Eyebrow from '@/components/ui/Eyebrow';
import Form from '@/components/ui/Form';
import Image from '@/components/ui/Image';
import Input from '@/components/ui/Input';
import Text from '@/components/ui/Text';
import Textarea from '@/components/ui/Textarea';
import Title from '@/components/ui/Title';
import useForm from '@/lib/hooks/useForm';
import type { TWithId } from '@/lib/interfaces/shared';
import Tag from '@/ui/Tag';
import { VACANCIES, type Vacancy } from './vacancies';

const Company: FC<TWithId> = ({ id }) => {
  const [openVacancyId, setOpenVacancyId] = useState<string | null>(null);
  const [cvName, setCvName] = useState<string>('Choose file or drag & drop');
  const [photoName, setPhotoName] = useState<string>('Optional: profile picture');
  const cvRef = useRef<HTMLInputElement | null>(null);
  const photoRef = useRef<HTMLInputElement | null>(null);

  type TVacancyFormData = {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    countryResidence: string;
    cityBirth: string;
    countryBirth: string;
    dob: string;
    linkedin?: string;
    intro: string;
    position?: string;
    vacancy_id?: string;
    vacancy_title?: string;
    cv?: File | null;
    photo?: File | null;
  };

  const defaultValues: TVacancyFormData = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    countryResidence: '',
    cityBirth: '',
    countryBirth: '',
    dob: '',
    linkedin: '',
    intro: '',
    position: '',
    vacancy_id: '',
    vacancy_title: '',
    cv: null,
    photo: null,
  };

  const vacancySchema = yup.object({
    firstName: yup.string().required('Required').trim(),
    lastName: yup.string().required('Required').trim(),
    email: yup.string().required('Required').email('Invalid email'),
    mobile: yup.string().required('Required').trim(),
    countryResidence: yup.string().required('Required').trim(),
    cityBirth: yup.string().required('Required').trim(),
    countryBirth: yup.string().required('Required').trim(),
    dob: yup.string().required('Required').trim(),
    linkedin: yup.string().url('Invalid URL').optional().nullable(),
    intro: yup.string().required('Required').trim(),
    position: yup.string().optional(),
    vacancy_id: yup.string().optional(),
    vacancy_title: yup.string().optional(),
    cv: yup
      .mixed<File>()
      .test('required', 'Required', (file) => !!file)
      .test('size', 'Max 10MB', (file) => !file || file.size <= 10 * 1024 * 1024)
      .test(
        'type',
        'PDF/DOC/DOCX only',
        (file) =>
          !file ||
          [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          ].includes(file.type),
      ),
    photo: yup
      .mixed<File>()
      .test('size', 'Max 5MB', (file) => !file || file.size <= 5 * 1024 * 1024)
      .nullable(),
  });

  const onSubmitVacancy = async (data: TVacancyFormData) => {
    try {
      const formData = new FormData();
      // Logical field names for Formspree
      formData.append('first_name', data.firstName);
      formData.append('last_name', data.lastName);
      formData.append('email', data.email);
      formData.append('mobile', data.mobile);
      formData.append('country_residence', data.countryResidence);
      formData.append('city_birth', data.cityBirth);
      formData.append('country_birth', data.countryBirth);
      formData.append('date_of_birth', data.dob);
      if (data.linkedin) formData.append('linkedin', data.linkedin);
      formData.append('intro', data.intro);
      if (openVacancy) {
        formData.append('vacancy_id', openVacancy.id);
        formData.append('vacancy_title', openVacancy.title);
      }
      if (data.cv) formData.append('cv_file', data.cv);
      if (data.photo) formData.append('photo_file', data.photo);

      const endpoint = 'https://formspree.io/f/xnnlqvqw';
      await fetch(endpoint, { method: 'POST', body: formData });
      resetFiles();
      vacancyForm.reset(defaultValues);
      return true;
    } catch {
      return false;
    }
  };

  const vacancyForm = useForm<TVacancyFormData>({
    schema: vacancySchema,
    defaultValues,
    onSubmit: onSubmitVacancy,
    onInvalid: () => {},
  });
  const { handleSubmit: handleVacancySubmit, setValue: setVacancyValue, reset: resetVacancy } = vacancyForm;

  const resetFiles = useCallback(() => {
    setCvName('Choose file or drag & drop');
    setPhotoName('Optional: profile picture');
    if (cvRef.current) cvRef.current.value = '';
    if (photoRef.current) photoRef.current.value = '';
  }, []);

  const handleOpenVacancy = (id: string) => {
    resetFiles();
    setOpenVacancyId(id);
  };

  const handleCloseModal = () => {
    setOpenVacancyId(null);
    resetFiles();
    resetVacancy(defaultValues);
  };

  useEffect(() => {
    if (openVacancyId) {
      // Ensure placeholders reset when opening a new vacancy
      resetFiles();
    }
  }, [
    openVacancyId, // Ensure placeholders reset when opening a new vacancy
    resetFiles,
  ]);

  const openVacancy: Vacancy | undefined = VACANCIES.find((v) => v.id === openVacancyId);

  return (
    <Tag
      tag='section'
      id={id}
      className='relative isolate overflow-hidden py-20 sm:py-28'
      inMotion
      data-section-title='Company'
      aria-labelledby='company-heading'
    >
      {/* soft skewed background accent (template-inspired) */}
      <div
        aria-hidden='true'
        className='pointer-events-none absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right -skew-x-12 bg-gray-200/75 ring-1 ring-info/10 shadow-xl shadow-info/10 dark:bg-gray-950/50 sm:-mr-80 lg:-mr-96'
      />

      {/* top fade to match bottom */}
      <div className='pointer-events-none absolute inset-x-0 top-0 z-0 h-24 sm:h-32 bg-gradient-to-b from-white via-white/60 to-transparent dark:from-gray-900 dark:via-gray-900/60 dark:to-transparent' />

      <Container>
        <div className='mx-auto grid max-w-[90rem] grid-cols-1 items-start gap-10 lg:grid-cols-2'>
          {/* Left: overline, title, body (sizes consistent with other blocks) */}
          <div className='mx-auto w-full max-w-2xl text-left'>
            <Eyebrow size='base' aria-hidden='true'>
              Company
            </Eyebrow>
            <Title
              level='2'
              id='company-heading'
              className='mt-2 text-4xl font-normal tracking-tight text-pretty text-secondary sm:text-5xl'
            >
              Industry know-how, engineered well
            </Title>
            <Text tag='p' className='mt-6 text-base/7 text-secondary-foreground'>
              Ten-plus years on real desks taught us what helps and what hurts. We pour that into software that’s
              practical and dependable, so brokers, traders, and suppliers can do great work without the noise.
            </Text>
            <Text tag='p' className='mt-4 text-base/7 text-secondary-foreground'>
              We’re based in Dubai, with an internal remote team across time zones, supporting customers worldwide. Join
              the team and share our passion for the industry.
            </Text>

            {/* Vacancy cards (support multiple) */}
            <div className='mt-6 space-y-4'>
              {VACANCIES.map((v) => (
                <div
                  key={v.id}
                  className='rounded-xl p-[1px] bg-gradient-to-r from-info/80 via-info/30 to-info/0 dark:from-info/80 dark:via-info/30 dark:to-info/0'
                >
                  <div className='rounded-[11px] bg-white/90 ring-1 ring-gray-900/10 backdrop-blur-sm dark:bg-gray-900/60 dark:ring-white/10'>
                    <div className='px-5 py-5 sm:px-6 sm:py-6'>
                      {/* Header row: meta + button */}
                      <div className='flex items-center justify-between gap-5'>
                        <div className='flex min-w-0 flex-1 flex-col'>
                          <Eyebrow as='p' className='font-medium'>
                            We’re hiring
                          </Eyebrow>
                          <h3 className='mt-1 text-lg font-semibold text-gray-900 dark:text-white'>{v.title}</h3>
                          {/* Badges row */}
                          <div className='mt-2 flex flex-wrap items-center gap-2'>
                            {/* Location/Remote from v.location split by • */}
                            {(() => {
                              const parts = (v.location || '').split('•').map((s) => s.trim());
                              const city = parts[0];
                              const remote = parts[1];
                              return (
                                <>
                                  {city ? (
                                    <span className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-secondary-foreground ring-1 ring-gray-900/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10'>
                                      <MapPinIcon className='h-3.5 w-3.5 text-info' aria-hidden='true' />
                                      <span>{city}</span>
                                    </span>
                                  ) : null}
                                  {remote ? (
                                    <span className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-secondary-foreground ring-1 ring-gray-900/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10'>
                                      <WifiIcon className='h-3.5 w-3.5 text-info' aria-hidden='true' />
                                      <span>{remote}</span>
                                    </span>
                                  ) : null}
                                </>
                              );
                            })()}
                            {v.type ? (
                              <span className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-secondary-foreground ring-1 ring-gray-900/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10'>
                                <BriefcaseIcon className='h-3.5 w-3.5 text-info' aria-hidden='true' />
                                <span>{v.type}</span>
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <Button size='sm' variant='primary' onClick={() => handleOpenVacancy(v.id)}>
                          Apply now
                        </Button>
                      </div>
                      {/* Full-width summary under the header row */}
                      {v.summary ? (
                        <p
                          className='mt-3 text-sm text-secondary-foreground'
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {v.summary}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: image with consistent sizing */}
          <div className='relative mx-auto w-full max-w-xl sm:mt-12 lg:mt-0 xl:mt-36'>
            <div className='group relative aspect-[6/5] w-full overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-black/5 dark:outline-white/10'>
              <Image
                src='cfimg:b3b7660b-dfbe-40a9-f9f8-61dfb2748000'
                alt='Our team'
                fill
                className='object-cover transition-transform duration-300 ease-out group-hover:scale-105'
                sizes='(max-width: 1024px) 100vw, 45vw'
                quality={90}
              />
            </div>
          </div>
        </div>
      </Container>

      {/* bottom fade like template */}
      <div className='pointer-events-none absolute inset-x-0 bottom-0 z-0 h-24 sm:h-32 bg-gradient-to-t from-white via-white/60 to-transparent dark:from-gray-900 dark:via-gray-900/60 dark:to-transparent' />

      {/* Vacancy modal */}
      <Dialog open={!!openVacancyId} onClose={handleCloseModal} className='relative z-50'>
        <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <DialogPanel className='relative w-full max-w-5xl rounded-lg sm:rounded-xl bg-white ring-1 ring-gray-200 shadow-xl dark:bg-gray-900 dark:ring-white/10 max-h-[90vh] overflow-y-auto sm:max-h-[85vh]'>
              <div className='flex items-center justify-between border-b border-gray-200 p-4 dark:border-white/10'>
                <Eyebrow size='base' className='m-0'>
                  Open position
                </Eyebrow>
                <button
                  type='button'
                  onClick={() => handleCloseModal()}
                  className='inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none dark:text-gray-300 dark:hover:bg-white/10'
                  aria-label='Close'
                >
                  <XMarkIcon className='h-5 w-5' aria-hidden='true' />
                </button>
              </div>
              <div className='grid gap-6 p-4 pt-3 sm:p-6 sm:pt-4 lg:grid-cols-2'>
                {/* Details column */}
                <div className='lg:max-h-[70vh] lg:overflow-y-auto lg:pr-2'>
                  <h3 className='mt-2 text-2xl font-semibold text-secondary dark:text-white'>{openVacancy?.title}</h3>
                  <p className='mt-1 text-sm text-secondary-foreground'>
                    {openVacancy?.location} • {openVacancy?.type}
                  </p>
                  {openVacancy?.summary ? (
                    <p className='mt-4 text-base/7 text-secondary-foreground'>{openVacancy.summary}</p>
                  ) : null}

                  {openVacancy?.responsibilities?.length ? (
                    <div className='mt-6'>
                      <h4 className='text-base font-semibold text-secondary dark:text-white'>Responsibilities</h4>
                      <ul className='mt-2 list-disc space-y-2 pl-5 text-sm/6 text-secondary-foreground'>
                        {openVacancy.responsibilities.map((r, i) => (
                          <li key={`resp-${i}`}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {openVacancy?.requirements?.length ? (
                    <div className='mt-6'>
                      <h4 className='text-base font-semibold text-secondary dark:text-white'>Requirements</h4>
                      <ul className='mt-2 list-disc space-y-2 pl-5 text-sm/6 text-secondary-foreground'>
                        {openVacancy.requirements.map((r, i) => (
                          <li key={`req-${i}`}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                {/* Form column */}
                <Form
                  form={vacancyForm.form}
                  onSubmit={handleVacancySubmit}
                  className='rounded-lg bg-primary p-4 ring-1 ring-gray-900/10 dark:ring-white/10'
                >
                  {/* Hidden context */}
                  <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                    <Input
                      name='firstName'
                      label={
                        <span>
                          First Name <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <div>
                      <Input
                        name='lastName'
                        label={
                          <span>
                            Last Name <span className='text-red-500'>*</span>
                          </span>
                        }
                      />
                    </div>
                    <Input
                      name='email'
                      type='email'
                      label={
                        <span>
                          Email <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <Input
                      name='mobile'
                      type='tel'
                      label={
                        <span>
                          Mobile Number <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <Input
                      name='countryResidence'
                      label={
                        <span>
                          Country of Residence <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <Input
                      name='cityBirth'
                      label={
                        <span>
                          City of Birth <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <Input
                      name='countryBirth'
                      label={
                        <span>
                          Country of Birth <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <Input
                      name='dob'
                      type='date'
                      label={
                        <span>
                          Date of Birth <span className='text-red-500'>*</span>
                        </span>
                      }
                    />
                    <Input name='linkedin' label='LinkedIn (optional)' />
                    <Textarea
                      name='intro'
                      label={
                        <span>
                          Introduce yourself <span className='text-red-500'>*</span>
                        </span>
                      }
                      rows={4}
                    />
                    <div>
                      <label className='text-sm font-medium text-secondary' htmlFor='cv'>
                        Upload CV <span className='text-red-500'>*</span>
                      </label>
                      <label
                        htmlFor='cv'
                        className='mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 bg-white px-3 py-3 text-sm text-secondary-foreground hover:border-info/60 dark:border-white/10 dark:bg-gray-800 dark:text-gray-200'
                      >
                        <span className='truncate pr-3'>{cvName}</span>
                        <span className='rounded-md bg-info px-3 py-1.5 text-white'>Browse</span>
                      </label>
                      <input
                        id='cv'
                        name='cv'
                        type='file'
                        required
                        accept='.pdf,.doc,.docx,.rtf'
                        className='sr-only'
                        ref={cvRef}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ? e.target.files[0] : null;
                          setCvName(file ? file.name : 'Choose file or drag & drop');
                          setVacancyValue('cv', file as File | null);
                        }}
                      />
                    </div>
                    <div>
                      <label className='text-sm font-medium text-secondary' htmlFor='photo'>
                        Upload Profile Picture
                      </label>
                      <label
                        htmlFor='photo'
                        className='mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 bg-white px-3 py-3 text-sm text-secondary-foreground hover:border-info/60 dark:border-white/10 dark:bg-gray-800 dark:text-gray-200'
                      >
                        <span className='truncate pr-3'>{photoName}</span>
                        <span className='rounded-md bg-info px-3 py-1.5 text-white'>Browse</span>
                      </label>
                      <input
                        id='photo'
                        name='photo'
                        type='file'
                        accept='image/*'
                        className='sr-only'
                        ref={photoRef}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ? e.target.files[0] : null;
                          setPhotoName(file ? file.name : 'Optional: profile picture');
                          setVacancyValue('photo', file as File | null);
                        }}
                      />
                    </div>
                  </div>
                  <div className='mt-6 flex items-center justify-between'>
                    <p className='text-xs text-secondary-foreground'>
                      You’re applying for <span className='font-medium'>{openVacancy?.title ?? ''}</span>
                    </p>
                    <Button type='submit' size='sm' variant='primary'>
                      Submit
                    </Button>
                  </div>
                </Form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </Tag>
  );
};

export default Company;
